import React, { useEffect, useState } from 'react';
import { setTrainerOnline } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Loader2, Radio } from 'lucide-react';
import { toast } from 'sonner';

/** Big online/offline switch for trainers. Reads & writes
 *  trainer_profile.is_online_now. Auto-syncs with /api/auth/me on mount. */
const TrainerOnlineToggle = () => {
  const { user, refreshUser } = useAuth();
  const initial = !!user?.trainer_profile?.is_online_now;
  const [on, setOn] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setOn(!!user?.trainer_profile?.is_online_now);
  }, [user?.trainer_profile?.is_online_now]);

  const handleToggle = async () => {
    const next = !on;
    setOn(next);
    setSaving(true);
    try {
      const res = await setTrainerOnline(next);
      await refreshUser();
      if (next && !res.data?.is_available_now) {
        toast.message(
          'You\u2019re online, but outside your scheduled hours.',
          { description: 'Clients will only see you as "Available Now" during your schedule.' }
        );
      } else {
        toast.success(next ? 'You\u2019re live for instant bookings' : 'Going offline');
      }
    } catch (e) {
      setOn(!next);
      const msg = e?.response?.data?.detail || 'Could not change status';
      toast.error(typeof msg === 'string' ? msg : 'Could not change status');
    } finally {
      setSaving(false);
    }
  };

  const isAvailableNow = on && (() => {
    const schedule = user?.trainer_profile?.weekly_schedule || {};
    const days = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    const d = new Date();
    const k = days[(d.getUTCDay() + 7) % 7 === 0 ? 0 : (d.getUTCDay())]; // crude UTC alignment
    const today = schedule[['mon','tue','wed','thu','fri','sat','sun'][d.getUTCDay() === 0 ? 6 : d.getUTCDay()-1]];
    if (!today?.enabled) return false;
    const [sh, sm] = (today.start || '00:00').split(':').map(Number);
    const [eh, em] = (today.end || '00:00').split(':').map(Number);
    const cur = d.getUTCHours() * 60 + d.getUTCMinutes();
    return cur >= sh*60+sm && cur < eh*60+em;
  })();

  return (
    <div
      className={`mb-6 rounded-lg border p-5 flex items-center justify-between ${
        on
          ? (isAvailableNow ? 'bg-emerald-500/5 border-emerald-500/40' : 'bg-amber-500/5 border-amber-500/40')
          : 'bg-[#18181B] border-zinc-800'
      }`}
      data-testid="trainer-online-toggle"
    >
      <div className="flex items-center gap-4">
        <div className="relative">
          <Radio className={`w-7 h-7 ${on ? (isAvailableNow ? 'text-emerald-400' : 'text-amber-400') : 'text-zinc-600'}`} />
          {on && isAvailableNow && (
            <span className="absolute top-0 right-0 inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400 animate-pulse" />
          )}
        </div>
        <div>
          <p className="text-white font-semibold leading-tight">
            {on ? (isAvailableNow ? 'You\u2019re live & Available Now' : 'You\u2019re online') : 'You\u2019re offline'}
          </p>
          <p className="text-xs text-zinc-400 mt-0.5">
            {on
              ? (isAvailableNow
                  ? 'Clients can send you instant booking requests right now.'
                  : 'You\u2019ll appear "Available Now" during your scheduled hours.')
              : 'Flip the switch to accept instant bookings within your schedule.'}
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={handleToggle}
        disabled={saving}
        role="switch"
        aria-checked={on}
        data-testid="online-toggle-switch"
        className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
          on ? 'bg-emerald-500' : 'bg-zinc-700'
        } disabled:opacity-60`}
      >
        <span
          className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
            on ? 'translate-x-7' : 'translate-x-1'
          }`}
        />
        {saving && <Loader2 className="w-3 h-3 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 animate-spin text-white" />}
      </button>
    </div>
  );
};

export default TrainerOnlineToggle;
